import React, { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';
import {
  authService,
  stockService,
  type StockItem,
  type CreateStockItemRequest,
  type UpdateStockItemRequest,
} from '../services';

interface StockItemFormData {
  name: string;
  sku?: string;
  unit: string;
  conversionUnit?: string;
  conversionRate?: string;
  minimumQuantity?: string;
  maximumQuantity?: string;
  category?: string;
  isActive: boolean;
  notes?: string;
}

const UNIT_OPTIONS = ['UN', 'CX', 'KG', 'G', 'L', 'ML', 'CUSTOM'];

const DEFAULT_FORM: StockItemFormData = {
  name: '',
  sku: '',
  unit: 'UN',
  conversionUnit: '',
  conversionRate: '',
  minimumQuantity: '',
  maximumQuantity: '',
  category: '',
  isActive: true,
  notes: '',
};

const StockItemsPage: React.FC = () => {
  const userRole = authService.getUserRole();
  const { showError, showSuccess } = useToast();

  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [cnpjFilter, setCnpjFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<StockItemFormData>(DEFAULT_FORM);
  const [editing, setEditing] = useState<StockItem | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<StockItem | null>(null);

  const canFilterByCnpj = userRole === 'DEVELOPER';

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await stockService.listStockItems({
        cnpj: canFilterByCnpj ? cnpjFilter.trim() || undefined : undefined,
        searchTerm: debouncedSearch || undefined,
        category: categoryFilter || undefined,
        isActive: activeFilter === 'all' ? undefined : activeFilter === 'active',
      });
      setItems(response);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar itens de estoque');
    } finally {
      setLoading(false);
    }
  }, [canFilterByCnpj, cnpjFilter, debouncedSearch, categoryFilter, activeFilter]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const categories = useMemo(() => {
    const unique = new Set(items.map((item) => item.category).filter(Boolean) as string[]);
    return Array.from(unique).sort();
  }, [items]);

  const handleOpenForm = (item?: StockItem) => {
    if (item) {
      setEditing(item);
      setFormData({
        name: item.name,
        sku: item.sku || '',
        unit: item.unit || 'UN',
        conversionUnit: item.conversionUnit || '',
        conversionRate: item.conversionRate ? String(item.conversionRate) : '',
        minimumQuantity: item.minimumQuantity ? String(item.minimumQuantity) : '',
        maximumQuantity: item.maximumQuantity ? String(item.maximumQuantity) : '',
        category: item.category || '',
        isActive: item.isActive,
        notes: item.notes || '',
      });
    } else {
      setEditing(null);
      setFormData(DEFAULT_FORM);
    }

    setFormError(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditing(null);
    setFormData(DEFAULT_FORM);
    setFormError(null);
  };

  const handleSubmitForm = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormLoading(true);
    setFormError(null);

    const parseNumberField = (value?: string): number | undefined => {
      if (!value || !value.trim()) return undefined;
      const parsed = Number(value.replace(',', '.'));
      return Number.isNaN(parsed) ? undefined : parsed;
    };

    const payload: CreateStockItemRequest | UpdateStockItemRequest = {
      name: formData.name,
      sku: formData.sku?.trim() || undefined,
      unit: formData.unit,
      conversionUnit: formData.conversionUnit?.trim() || undefined,
      conversionRate: parseNumberField(formData.conversionRate),
      minimumQuantity: parseNumberField(formData.minimumQuantity),
      maximumQuantity: parseNumberField(formData.maximumQuantity),
      category: formData.category?.trim() || undefined,
      isActive: formData.isActive,
      notes: formData.notes?.trim() || undefined,
    };

    try {
      if (editing) {
        await stockService.updateStockItem(editing.id, payload);
        showSuccess('Item de estoque atualizado com sucesso.');
      } else {
        await stockService.createStockItem(payload as CreateStockItemRequest);
        showSuccess('Item de estoque criado com sucesso.');
      }

      handleCloseForm();
      loadItems();
    } catch (err: any) {
      setFormError(err.message || 'Erro ao salvar item de estoque');
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenDeleteDialog = (item: StockItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      await stockService.deleteStockItem(itemToDelete.id, {
        cnpj: canFilterByCnpj ? cnpjFilter.trim() || undefined : undefined,
      });
      showSuccess(`Item "${itemToDelete.name}" removido do estoque.`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      loadItems();
    } catch (err: any) {
      showError(err.message || 'Erro ao remover item de estoque');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Itens de Estoque
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Cadastre ingredientes e insumos utilizados na produção e monte fichas técnicas.
          </Typography>
        </Box>

        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenForm()}>
          Novo item
        </Button>
      </Box>

      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="flex-start">
          <TextField
            label="Buscar por nome ou SKU"
            value={searchTerm}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FilterListIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', md: 320 } }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={categoryFilter}
              label="Categoria"
              onChange={(event: SelectChangeEvent<string>) => setCategoryFilter(event.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              label="Status"
              onChange={(event: SelectChangeEvent<string>) =>
                setActiveFilter(event.target.value as typeof activeFilter)
              }
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Apenas ativos</MenuItem>
              <MenuItem value="inactive">Apenas inativos</MenuItem>
            </Select>
          </FormControl>

          {canFilterByCnpj && (
            <TextField
              label="CNPJ"
              value={cnpjFilter}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setCnpjFilter(event.target.value)}
              size="small"
              placeholder="Somente números"
              sx={{ width: { xs: '100%', md: 220 } }}
            />
          )}

          <Box display="flex" gap={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadItems}>
              Atualizar
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={1}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Unidade</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell align="right">Mínimo</TableCell>
                  <TableCell align="right">Máximo</TableCell>
                  <TableCell>Ativo</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="subtitle2">{item.name}</Typography>
                        {item.notes && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {item.notes}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{item.sku || '-'}</TableCell>
                    <TableCell>{item.unit || '-'}</TableCell>
                    <TableCell>{item.category || '-'}</TableCell>
                    <TableCell align="right">
                      {item.minimumQuantity !== undefined
                        ? item.minimumQuantity.toLocaleString('pt-BR')
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {item.maximumQuantity !== undefined
                        ? item.maximumQuantity.toLocaleString('pt-BR')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.isActive ? 'Sim' : 'Não'}
                        color={item.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenForm(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(item)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum item de estoque encontrado para os filtros selecionados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar item de estoque' : 'Novo item de estoque'}</DialogTitle>
        <form onSubmit={handleSubmitForm}>
          <DialogContent dividers>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}

            <Box display="flex" flexWrap="wrap" gap={2}>
              <TextField
                label="Nome"
                value={formData.name}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, name: event.target.value }))
                }
                fullWidth
                required
                disabled={formLoading}
                margin="dense"
                sx={{ flex: '1 1 280px' }}
              />

              <TextField
                label="SKU"
                value={formData.sku}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, sku: event.target.value }))
                }
                margin="dense"
                disabled={formLoading}
                sx={{ flex: '1 1 200px' }}
              />

              <FormControl sx={{ flex: '1 1 180px' }} margin="dense" disabled={formLoading}>
                <InputLabel>Unidade</InputLabel>
                <Select
                  value={formData.unit}
                  label="Unidade"
                  onChange={(event: SelectChangeEvent<string>) =>
                    setFormData((prev) => ({ ...prev, unit: event.target.value }))
                  }
                >
                  {UNIT_OPTIONS.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: '1 1 200px' }} margin="dense" disabled={formLoading}>
                <InputLabel>Unidade conversão</InputLabel>
                <Select
                  value={formData.conversionUnit}
                  label="Unidade conversão"
                  onChange={(event: SelectChangeEvent<string>) =>
                    setFormData((prev) => ({ ...prev, conversionUnit: event.target.value }))
                  }
                >
                  <MenuItem value="">
                    <em>Nenhuma</em>
                  </MenuItem>
                  {UNIT_OPTIONS.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Taxa de conversão"
                value={formData.conversionRate}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, conversionRate: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
                placeholder="ex: 12"
                helperText="Quantidade de unidades básicas equivalentes"
                sx={{ flex: '1 1 200px' }}
              />

              <TextField
                label="Qtd mínima"
                value={formData.minimumQuantity}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, minimumQuantity: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
                placeholder="ex: 10"
                sx={{ flex: '1 1 160px' }}
              />

              <TextField
                label="Qtd máxima"
                value={formData.maximumQuantity}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, maximumQuantity: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
                placeholder="ex: 200"
                sx={{ flex: '1 1 160px' }}
              />

              <TextField
                label="Categoria"
                value={formData.category}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, category: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
                placeholder="ex: Carnes, Embalagens"
                sx={{ flex: '1 1 240px' }}
              />

              <TextField
                label="Observações"
                value={formData.notes}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, notes: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
                multiline
                minRows={2}
                fullWidth
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData((prev) => ({ ...prev, isActive: event.target.checked }))
                    }
                    disabled={formLoading}
                  />
                }
                label="Item ativo"
              />
            </Box>

          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm} disabled={formLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={formLoading}>
              {formLoading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover item de estoque</DialogTitle>
        <DialogContent dividers>
          Confirma remover o item "{itemToDelete?.name}"? Esta ação não pode ser desfeita.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StockItemsPage;
